import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { assignTicket, getUsers } from '@/services/api';
import { useAuth } from '@/services/auth';

interface Props {
  ticketId: string;
  currentOwnerUserId: string | null;
}

export default function AssignOwner({ ticketId, currentOwnerUserId }: Props) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const isEmployee = user?.role === 1;

  const { data: teamUsers } = useQuery({
    queryKey: ['users'],
    queryFn: getUsers,
    enabled: !isEmployee,
  });

  // Employees can only assign to themselves; managers/admins get the full team list.
  const options = isEmployee && user?.userId
    ? [{ userId: user.userId, displayName: user.displayName }]
    : (teamUsers ?? []);

  const assign = useMutation({
    mutationFn: (newUserId: string) => assignTicket(ticketId, newUserId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ticket', ticketId] }),
  });

  return (
    <select
      value={currentOwnerUserId ?? ''}
      onChange={e => assign.mutate(e.target.value)}
      disabled={assign.isPending}
      className="w-full rounded-md border border-brand-border bg-white px-3 py-2 text-sm text-brand-text-primary disabled:opacity-50"
      aria-label="Assign ticket owner"
      title="Assign ticket owner"
    >
      <option value="">Unassigned</option>
      {options.map(u => (
        <option key={u.userId} value={u.userId}>{u.displayName}</option>
      ))}
    </select>
  );
}
